import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings2 } from 'lucide-react';
import { BudgetCard } from './budget-card';
import { ColumnConfiguratorModal } from '@/components/column-configurator-modal';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { 
  RESOURCE_BUDGET_COLUMNS, 
  RESOURCE_BUDGET_PRESETS,
  RESOURCE_BUDGET_STORAGE_KEY 
} from '@/lib/resource-budget-columns';
import { 
  formatHours, 
  formatCurrency, 
  formatPercent,
  getResourceBudgetStatus,
  getResourceBudgetStatusLabel,
  getResourceBudgetStatusColor
} from '@/lib/budget-utils';
import type { ResourceBudget } from '@/types/budget.types';

interface ResourceBudgetViewProps {
  data: ResourceBudget;
}

export function ResourceBudgetView({ data }: ResourceBudgetViewProps) {
  console.log('[ResourceBudgetView] Received data:', data);

  if (!data || !data.summary || !data.byRole) {
    console.log('[ResourceBudgetView] Missing required data:', {
      hasData: !!data,
      hasSummary: !!data?.summary,
      hasByRole: !!data?.byRole,
    });
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No resource budget data available</p>
      </div>
    );
  }

  const { summary, byRole } = data;
  console.log('[ResourceBudgetView] Summary:', summary);
  console.log('[ResourceBudgetView] ByRole count:', byRole.length);

  // Calculate totals from summary (use existing values if available)
  const totalBudgetCost = summary.totalBudgetCost || byRole.reduce((sum, role) => sum + role.budgetCost, 0);
  const totalActualCost = summary.totalActualCost || byRole.reduce((sum, role) => sum + role.actualCost, 0);

  return (
    <div className="space-y-6">
      {/* Hours Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Budget"
          primaryValue={formatHours(summary.totalBudget)}
          secondaryValue={formatCurrency(totalBudgetCost)}
          status="green"
          variant="compact"
        />
        <BudgetCard
          title="Actual"
          primaryValue={formatHours(summary.totalActual)}
          secondaryValue={`${formatPercent(summary.totalActual / summary.totalBudget)} • ${formatCurrency(totalActualCost)}`}
          status={summary.totalActual > summary.totalBudget ? 'red' : summary.totalActual > summary.totalBudget * 0.9 ? 'amber' : 'green'}
          variant="compact"
        />
        <BudgetCard
          title="Remaining"
          primaryValue={formatHours(summary.totalRemaining)}
          secondaryValue={`${formatPercent(summary.totalRemaining / summary.totalBudget)} • ${formatCurrency(totalBudgetCost - totalActualCost)}`}
          status={summary.totalRemaining < 0 ? 'red' : summary.totalRemaining < summary.totalBudget * 0.1 ? 'amber' : 'green'}
          variant="compact"
        />
        <BudgetCard
          title="Billable %"
          primaryValue={formatPercent(summary.billablePercent / 100)}
          secondaryValue={`target: ${formatPercent(summary.targetBillablePercent / 100)}`}
          status={
            summary.billablePercent >= summary.targetBillablePercent
              ? 'green'
              : summary.billablePercent >= summary.targetBillablePercent - 5
              ? 'amber'
              : 'red'
          }
          variant="compact"
        />
      </div>

      {/* Resource Breakdown by Role */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Resource Breakdown by Role</CardTitle>
          <ColumnConfigButton />
        </CardHeader>
        <CardContent>
          <HoursByRoleView data={byRole} />
        </CardContent>
      </Card>
    </div>
  );
}

// Column Configuration Button Component
function ColumnConfigButton() {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: RESOURCE_BUDGET_STORAGE_KEY,
    defaultColumns: RESOURCE_BUDGET_COLUMNS,
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsConfigOpen(true)}
        className="gap-2"
      >
        <Settings2 className="h-4 w-4" />
        Configure Columns
      </Button>
      <ColumnConfiguratorModal
        open={isConfigOpen}
        onOpenChange={setIsConfigOpen}
        columns={columns}
        onSave={updateColumns}
        presets={RESOURCE_BUDGET_PRESETS}
        defaultColumns={RESOURCE_BUDGET_COLUMNS}
      />
    </>
  );
}

// Hours by Role View
function HoursByRoleView({ data }: { data: ResourceBudget['byRole'] }) {
  const { columns, isLoading } = useColumnPreferences({
    storageKey: RESOURCE_BUDGET_STORAGE_KEY,
    defaultColumns: RESOURCE_BUDGET_COLUMNS,
  });

  if (isLoading) {
    return <p className="text-muted-foreground text-center py-8">Loading...</p>;
  }

  if (!data || data.length === 0) {
    return <p className="text-muted-foreground text-center py-8">No role data available</p>;
  }

  // Get visible columns in order
  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  const rows = data.map((role) => {
    const status = getResourceBudgetStatus(role.actualHours, role.budgetHours);
    const statusColors = getResourceBudgetStatusColor(status);
    const statusLabel = getResourceBudgetStatusLabel(status);
    const varianceHours = role.actualHours - role.budgetHours;
    const varianceCost = role.actualCost - role.budgetCost;
    
    return {
      id: role.role,
      role: role.role,
      'budget-hours': formatHours(role.budgetHours),
      'budget-cost': formatCurrency(role.budgetCost),
      'actual-hours': formatHours(role.actualHours),
      'actual-cost': formatCurrency(role.actualCost),
      'remaining-hours': formatHours(role.remainingHours),
      'percent-used': formatPercent(role.percentUsed / 100),
      rate: formatCurrency(role.billRate) + '/hr',
      status: { label: statusLabel, colors: statusColors },
      'variance-hours': formatHours(varianceHours),
      'variance-cost': formatCurrency(varianceCost),
      'forecast-hours': formatHours(role.budgetHours), // Placeholder - would need actual forecast data
      'forecast-cost': formatCurrency(role.budgetCost), // Placeholder - would need actual forecast data
      efficiency: '100%', // Placeholder - would need actual efficiency calculation
      // Raw values for totals calculation
      _raw: {
        budgetHours: role.budgetHours,
        actualHours: role.actualHours,
        remainingHours: role.remainingHours,
        budgetCost: role.budgetCost,
        actualCost: role.actualCost,
        varianceHours,
        varianceCost,
      },
    };
  });

  // Calculate totals from raw values
  const totals = rows.reduce(
    (acc, row) => ({
      budgetHours: acc.budgetHours + row._raw.budgetHours,
      actualHours: acc.actualHours + row._raw.actualHours,
      remainingHours: acc.remainingHours + row._raw.remainingHours,
      budgetCost: acc.budgetCost + row._raw.budgetCost,
      actualCost: acc.actualCost + row._raw.actualCost,
      varianceHours: acc.varianceHours + row._raw.varianceHours,
      varianceCost: acc.varianceCost + row._raw.varianceCost,
    }),
    { 
      budgetHours: 0, 
      actualHours: 0, 
      remainingHours: 0, 
      budgetCost: 0, 
      actualCost: 0,
      varianceHours: 0,
      varianceCost: 0,
    }
  );

  // Helper function to render cell content based on column ID
  const renderCell = (columnId: string, row: typeof rows[0]) => {
    if (columnId === 'status') {
      return (
        <Badge className={row.status.colors.badge}>
          {row.status.label}
        </Badge>
      );
    }
    return row[columnId as keyof typeof row] as string;
  };

  // Helper function to render total cell content based on column ID
  const renderTotalCell = (columnId: string) => {
    switch (columnId) {
      case 'role':
        return 'TOTAL';
      case 'budget-hours':
        return formatHours(totals.budgetHours);
      case 'budget-cost':
        return formatCurrency(totals.budgetCost);
      case 'actual-hours':
        return formatHours(totals.actualHours);
      case 'actual-cost':
        return formatCurrency(totals.actualCost);
      case 'remaining-hours':
        return formatHours(totals.remainingHours);
      case 'percent-used':
        return formatPercent(totals.actualHours / totals.budgetHours);
      case 'variance-hours':
        return formatHours(totals.varianceHours);
      case 'variance-cost':
        return formatCurrency(totals.varianceCost);
      case 'forecast-hours':
        return formatHours(totals.budgetHours);
      case 'forecast-cost':
        return formatCurrency(totals.budgetCost);
      case 'status':
        const totalStatus = getResourceBudgetStatus(totals.actualHours, totals.budgetHours);
        return (
          <Badge className={getResourceBudgetStatusColor(totalStatus).badge}>
            {getResourceBudgetStatusLabel(totalStatus)}
          </Badge>
        );
      default:
        return '';
    }
  };

  // Helper function to get alignment class
  const getAlignmentClass = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (columnId === 'status') return 'text-center';
    return column?.align === 'right' ? 'text-right' : '';
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {visibleColumns.map((column) => (
              <TableHead 
                key={column.id} 
                className={getAlignmentClass(column.id)}
              >
                {column.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {visibleColumns.map((column) => (
                <TableCell 
                  key={column.id} 
                  className={`${column.id === 'role' ? 'font-medium' : ''} ${getAlignmentClass(column.id)}`}
                >
                  {renderCell(column.id, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow className="font-bold bg-muted/50">
            {visibleColumns.map((column) => (
              <TableCell 
                key={column.id} 
                className={getAlignmentClass(column.id)}
              >
                {renderTotalCell(column.id)}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
